
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Sparkles, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';

interface AIDescriptionGeneratorProps {
  title: string;
  category: string;
  subcategory: string;
  condition: string;
  onGeneratedDescription: (description: string) => void;
}

export const AIDescriptionGenerator: React.FC<AIDescriptionGeneratorProps> = ({
  title,
  category,
  subcategory,
  condition,
  onGeneratedDescription
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const { toast } = useToast();
  const { profile } = useAuth();
  
  const generateDescription = async () => {
    if (!title) {
      toast({
        title: "Titulli mungon",
        description: "Ju lutemi shkruani një titull përpara se të gjeneroni një përshkrim",
        variant: "destructive"
      });
      return;
    }
    
    try {
      setIsGenerating(true);
      
      // Get category and subcategory names
      let categoryName = category;
      let subcategoryName = subcategory;
      
      try {
        // Only make these queries if we have IDs
        if (category && !isNaN(parseInt(category, 10))) {
          const { data: categoryData } = await supabase
            .from('categories')
            .select('name')
            .eq('id', parseInt(category, 10))
            .single();
            
          if (categoryData) categoryName = categoryData.name;
        }
        
        if (subcategory && !isNaN(parseInt(subcategory, 10))) {
          const { data: subcategoryData } = await supabase
            .from('subcategories')
            .select('name')
            .eq('id', parseInt(subcategory, 10))
            .single();
            
          if (subcategoryData) subcategoryName = subcategoryData.name;
        }
      } catch (error) {
        console.error("Error fetching category/subcategory names:", error);
        // Continue with the IDs if names can't be fetched
      }
      
      const { data, error } = await supabase.functions.invoke('generate-listing-description', {
        body: {
          title,
          category: categoryName,
          subcategory: subcategoryName,
          condition,
          // Fix: Use a default language 'sq' if profile doesn't have language property
          language: 'sq' // Default to Albanian
        }
      });
      
      if (error) {
        throw new Error(`Gabim gjatë gjenerimit të përshkrimit: ${error.message}`);
      }
      
      if (data?.description) {
        onGeneratedDescription(data.description);
        toast({
          title: "Përshkrim i gjeneruar",
          description: "Përshkrimi u gjenerua me sukses duke përdorur AI"
        });
      } else {
        throw new Error("Nuk u mor asnjë përshkrim nga AI");
      }
    } catch (error) {
      console.error("Error generating description:", error);
      toast({
        title: "Gabim",
        description: error.message || "Gabim gjatë gjenerimit të përshkrimit të produktit",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };
  
  return (
    <Button 
      type="button" 
      variant="outline" 
      onClick={generateDescription} 
      disabled={isGenerating || !title}
      className="w-full mt-2 bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200 hover:bg-gradient-to-r hover:from-indigo-100 hover:to-purple-100"
    >
      {isGenerating ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Gjenerimi...
        </>
      ) : (
        <>
          <Sparkles className="mr-2 h-4 w-4 text-indigo-500" />
          Gjenero përshkrim me AI
        </>
      )}
    </Button>
  );
};

export default AIDescriptionGenerator;
